/**
 * ResultVideoPlayer — the polished, in-house player for finished videos.
 * Replaces the browser's default <video controls> with a branded control bar:
 * play/pause, seekable scrubber with buffered range, current/total time,
 * volume + mute, and fullscreen. Controls auto-hide during playback and
 * respond to keyboard shortcuts (space, ← →, ↑ ↓, M, F).
 *
 * Fills its container — the parent sets the aspect ratio / max height.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, Loader2, Captions, CaptionsOff } from 'lucide-react';
import { cn } from '@/v2/utils';
import { trackVideoPlayed } from '@/services/analyticsService';

/** Seconds → m:ss (the player's clock; also the duration badge on result cards). */
export function fmt(t: number): string {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function ResultVideoPlayer({ src, poster, autoPlay, className, burnedSrc, onCaptionsChange, onRequestClean, operationType = 'video' }: {
  /** Base video to play — the CLEAN (no-captions) rendition. */
  src: string;
  poster?: string;
  autoPlay?: boolean;
  className?: string;
  /** Captioned (burned-in) rendition — the CC button swaps to this. */
  burnedSrc?: string;
  /** Reports the captions on/off state so the parent can pick the right
   *  download file (captioned vs clean). */
  onCaptionsChange?: (on: boolean) => void;
  /** LongVid: only ONE file is delivered, so there is nothing to swap to yet. When
   *  this is given the CC button still shows; the first press asks for the clean
   *  rendition (a spinner while it renders) and resolves with its url — the parent
   *  then feeds it back as `src` and the usual swap takes over. Null = not now. */
  onRequestClean?: () => Promise<string | null>;
  /** Analytics dimension for the video-played event. Defaults to 'video'. */
  operationType?: string;
}) {
  const { t } = useTranslation('v2');
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fire the video-played analytics event only on the FIRST play, once per mount
  // (so pause/resume and re-renders never double-count).
  const playedRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [controlsShown, setControlsShown] = useState(true);

  // ----- Captions -----
  // The CC button SWAPS the actual video source between HeyGen's two renditions:
  // the clean `src` (no captions) and the burned-in captioned `burnedSrc`. The
  // toggle shows whenever both exist; there is no SRT/soft-track path.
  const [captionsOn, setCaptionsOn] = useState(true);
  const resumeRef = useRef<{ time: number; play: boolean } | null>(null);
  const canToggle = !!burnedSrc && burnedSrc !== src;
  const playedSrc = canToggle && captionsOn ? burnedSrc! : src;

  // Tell the parent (for download parity) whenever captions state is known.
  useEffect(() => { onCaptionsChange?.(captionsOn); }, [captionsOn, onCaptionsChange]);

  const toggleCaptions = useCallback(() => {
    const v = videoRef.current;
    // Swapping the source reloads the video → remember position so it resumes.
    if (v) resumeRef.current = { time: v.currentTime, play: !v.paused };
    setCaptionsOn((o) => !o);
  }, []);

  // FIRST press when there is nothing to swap to yet: have the clean rendition made,
  // then take the ordinary toggle path (resume position included) once it lands.
  const [requestingClean, setRequestingClean] = useState(false);
  const requestClean = useCallback(async () => {
    if (requestingClean || !onRequestClean) return;
    setRequestingClean(true);
    try {
      if (await onRequestClean()) toggleCaptions();
    } catch { /* not available — captions simply stay on */ }
    setRequestingClean(false);
  }, [onRequestClean, requestingClean, toggleCaptions]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => undefined);
    else v.pause();
  }, []);

  const seekBy = useCallback((delta: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = Math.max(0, Math.min(v.duration, v.currentTime + delta));
  }, []);

  const setVol = useCallback((value: number) => {
    const v = videoRef.current;
    if (!v) return;
    const clamped = Math.max(0, Math.min(1, value));
    v.volume = clamped;
    v.muted = clamped === 0;
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted && v.volume === 0) v.volume = 0.5;
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = wrapRef.current as (HTMLElement & { webkitRequestFullscreen?: () => void }) | null;
    const v = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    const doc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
    // Already fullscreen → exit (standard or webkit).
    if (document.fullscreenElement || doc.webkitFullscreenElement) {
      if (document.exitFullscreen) void document.exitFullscreen().catch(() => undefined);
      else doc.webkitExitFullscreen?.();
      return;
    }
    // Desktop / Android / iPad / macOS Safari: fullscreen the WRAPPER (keeps our custom controls).
    if (el?.requestFullscreen) { void el.requestFullscreen().catch(() => undefined); return; }
    if (el?.webkitRequestFullscreen) { el.webkitRequestFullscreen(); return; }
    // iPhone Safari has NO element fullscreen API — only the <video> can go fullscreen, via
    // webkitEnterFullscreen (shows iOS's native player). This is the path that was missing, so
    // tapping fullscreen on iPhone did nothing.
    v?.webkitEnterFullscreen?.();
  }, []);

  // Auto-hide controls a moment after the pointer goes idle (while playing).
  const nudgeControls = useCallback(() => {
    setControlsShown(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (!videoRef.current?.paused) setControlsShown(false);
    }, 2600);
  }, []);

  useEffect(() => {
    const doc = document as Document & { webkitFullscreenElement?: Element };
    const onFs = () => setFullscreen(!!(document.fullscreenElement || doc.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    // iPhone fires these on the <video> itself (native fullscreen, no document API).
    const v = videoRef.current;
    const onBegin = () => setFullscreen(true);
    const onEnd = () => setFullscreen(false);
    v?.addEventListener('webkitbeginfullscreen', onBegin);
    v?.addEventListener('webkitendfullscreen', onEnd);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
      v?.removeEventListener('webkitbeginfullscreen', onBegin);
      v?.removeEventListener('webkitendfullscreen', onEnd);
    };
  }, []);

  // Keyboard shortcuts while the player is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      switch (e.key) {
        case ' ': case 'k': e.preventDefault(); togglePlay(); break;
        case 'ArrowLeft': seekBy(-5); break;
        case 'ArrowRight': seekBy(5); break;
        case 'ArrowUp': e.preventDefault(); setVol((videoRef.current?.volume ?? 1) + 0.1); break;
        case 'ArrowDown': e.preventDefault(); setVol((videoRef.current?.volume ?? 1) - 0.1); break;
        case 'm': toggleMute(); break;
        case 'f': toggleFullscreen(); break;
      }
      nudgeControls();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, seekBy, setVol, toggleMute, toggleFullscreen, nudgeControls]);

  const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (parseFloat(e.target.value) / 100) * v.duration;
  };

  const progressPct = duration ? (current / duration) * 100 : 0;
  const bufferedPct = duration ? (buffered / duration) * 100 : 0;

  return (
    <div
      ref={wrapRef}
      className={cn('relative w-full h-full bg-black group/vp select-none', className)}
      onMouseMove={nudgeControls}
      onMouseLeave={() => { if (!videoRef.current?.paused) setControlsShown(false); }}
    >
      <video
        ref={videoRef}
        src={playedSrc}
        poster={poster}
        autoPlay={autoPlay}
        playsInline
        className="w-full h-full object-contain"
        onClick={togglePlay}
        onPlay={() => {
          setPlaying(true);
          nudgeControls();
          if (!playedRef.current) { playedRef.current = true; trackVideoPlayed(operationType); }
        }}
        onPause={() => { setPlaying(false); setControlsShown(true); }}
        onWaiting={() => setWaiting(true)}
        onPlaying={() => setWaiting(false)}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration);
          setVolume(e.currentTarget.volume);
          // Resume position after a captions file-swap (swap-fallback mode).
          const r = resumeRef.current;
          if (r) {
            e.currentTarget.currentTime = r.time;
            if (r.play) void e.currentTarget.play().catch(() => undefined);
            resumeRef.current = null;
          }
        }}
        onVolumeChange={(e) => { setVolume(e.currentTarget.volume); setMuted(e.currentTarget.muted); }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          setCurrent(v.currentTime);
          try {
            const b = v.buffered;
            if (b.length) setBuffered(b.end(b.length - 1));
          } catch { /* buffered not ready */ }
        }}
      />

      {/* Center play / buffering affordance */}
      {(!playing || waiting) && (
        <button
          onClick={togglePlay}
          aria-label={playing ? t('resultVideoPlayer.buffering') : t('resultVideoPlayer.play')}
          className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white hover:scale-105 transition-transform"
        >
          {waiting
            ? <Loader2 className="w-7 h-7 animate-spin" />
            : <Play className="w-8 h-8 ml-1 fill-white" />}
        </button>
      )}

      {/* Control bar */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 px-3 pb-2.5 pt-8 bg-gradient-to-t from-black/80 via-black/30 to-transparent transition-opacity duration-200',
          controlsShown ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        {/* Scrubber */}
        <div className="relative h-4 flex items-center group/bar">
          <div className="absolute inset-x-0 h-1 rounded-full bg-white/25 overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-white/30" style={{ width: `${bufferedPct}%` }} />
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-accent" style={{ width: `${progressPct}%` }} />
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={progressPct}
            onChange={onScrub}
            aria-label={t('resultVideoPlayer.seek')}
            className="absolute inset-x-0 w-full appearance-none bg-transparent cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:opacity-0 group-hover/bar:[&::-webkit-slider-thumb]:opacity-100"
          />
        </div>

        {/* Buttons row */}
        <div className="flex items-center gap-3 mt-1 text-white">
          <button onClick={togglePlay} aria-label={playing ? t('resultVideoPlayer.pause') : t('resultVideoPlayer.play')} className="hover:text-primary transition-colors">
            {playing ? <Pause className="w-5 h-5 fill-white" /> : <Play className="w-5 h-5 fill-white" />}
          </button>

          {/* Volume */}
          <div className="flex items-center gap-1.5 group/vol">
            <button onClick={toggleMute} aria-label={muted ? t('resultVideoPlayer.unmute') : t('resultVideoPlayer.mute')} className="hover:text-primary transition-colors">
              {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={(e) => setVol(parseFloat(e.target.value))}
              aria-label={t('resultVideoPlayer.volume')}
              className="w-0 group-hover/vol:w-16 transition-[width] duration-200 h-1 appearance-none rounded-full bg-white/30 cursor-pointer accent-white [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
            />
          </div>

          <span className="text-xs font-medium tabular-nums text-white/90">
            {fmt(current)} <span className="text-white/50">/ {fmt(duration)}</span>
          </span>

          <div className="flex-1" />

          {(canToggle || !!onRequestClean) && (
            <button
              onClick={canToggle ? toggleCaptions : () => void requestClean()}
              disabled={requestingClean}
              aria-label={captionsOn ? t('resultVideoPlayer.hideCaptions') : t('resultVideoPlayer.showCaptions')}
              title={captionsOn ? t('resultVideoPlayer.captionsOn') : t('resultVideoPlayer.captionsOff')}
              className={cn('transition-colors disabled:opacity-70', captionsOn ? 'text-primary' : 'text-white hover:text-primary')}
            >
              {requestingClean
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : captionsOn ? <Captions className="w-5 h-5" /> : <CaptionsOff className="w-5 h-5" />}
            </button>
          )}

          <button onClick={toggleFullscreen} aria-label={fullscreen ? t('resultVideoPlayer.exitFullscreen') : t('resultVideoPlayer.fullscreen')} className="hover:text-primary transition-colors">
            {fullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
